import React, { useEffect, useRef, useState } from 'react';
import unicornLogo from '../../assets/unicorn-logo.png';
import unicornLogoSvg from '../../assets/unicorn-logo-traced.svg';
import { useBrassLogo } from '../../components/UI/useBrassLogo';
import { createHeroLogoScene, MODEL_FRAME } from './heroLogoScene';

const LOGO_ASPECT = 958 / 1563; // width / height
const STAR_COUNT = 260;

// Where the stars should converge, as fractions of the hero rect. Default is
// the logo's desktop position (right: 8%, top: 14%, height: 72%); used only as
// a fallback before the real element can be measured.
const DEFAULT_LOGO_BOX = {
  nx: 0.92 - 0.72 * LOGO_ASPECT,
  ny: 0.14,
  nw: 0.72 * LOGO_ASPECT,
  nh: 0.72,
};

// Read the logo element's real position relative to the hero, normalized to
// fractions of the hero's size — so the star targets track the logo wherever
// CSS puts it instead of a hardcoded right:8% box. Returns null (caller falls
// back to DEFAULT_LOGO_BOX) until both elements are laid out.
function readLogoBox(wrap, logo) {
  if (!wrap || !logo) return null;
  const wr = wrap.getBoundingClientRect();
  const lr = logo.getBoundingClientRect();
  if (!wr.width || !wr.height || !lr.width) return null;
  // The 3D mark is drawn smaller than its box (camera pull-back), so aim at
  // the area it actually covers or the stars settle visibly around it.
  const k = getUse3DLogo() ? 1 / MODEL_FRAME : 1;
  const w = lr.width * k;
  const h = lr.height * k;
  return {
    nx: (lr.left + (lr.width - w) / 2 - wr.left) / wr.width,
    ny: (lr.top + (lr.height - h) / 2 - wr.top) / wr.height,
    nw: w / wr.width,
    nh: h / wr.height,
  };
}

// Candidate silhouette points at a given margin: opaque at the point itself,
// and still opaque `margin` pixels out in each direction — i.e. solidly
// inside the mass, not just past the alpha threshold right at an edge. A
// star's glow has real radius, so a target sampled exactly on the boundary
// (or in a thin sliver — between the ears, a mane strand) is still visible
// once it arrives even though the point it aimed at was technically opaque.
function silhouetteCandidates(data, cw, ch, margin, stride) {
  const opaque = (x, y) => x >= 0 && y >= 0 && x < cw && y < ch && data[(y * cw + x) * 4 + 3] > 100;
  const out = [];
  for (let y = 0; y < ch; y += stride) {
    for (let x = 0; x < cw; x += stride) {
      if (!opaque(x, y)) continue;
      if (margin > 0 && !(opaque(x - margin, y) && opaque(x + margin, y) && opaque(x, y - margin) && opaque(x, y + margin))) continue;
      out.push({ x, y });
    }
  }
  return out;
}

// Loads the logo asset once and samples random points from inside its opaque
// silhouette — these become the convergence targets for the stars. Runs in the
// background; the starfield streams without them until they're ready.
function useLogoPoints(count) {
  const [points, setPoints] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.src = unicornLogo;
    img.onload = () => {
      const idle = window.requestIdleCallback
        ? (cb) => window.requestIdleCallback(cb, { timeout: 120 })
        : (cb) => setTimeout(cb, 0);
      idle(() => {
        if (cancelled) return;
        const cw = 300;
        const ch = Math.round(cw / LOGO_ASPECT);
        const off = document.createElement('canvas');
        off.width = cw;
        off.height = ch;
        const octx = off.getContext('2d');
        octx.drawImage(img, 0, 0, cw, ch);
        const data = octx.getImageData(0, 0, cw, ch).data;
        const stride = 2;
        // Deep margin so targets land well inside the solid mass, not just
        // past the edge — the star's approach is a straight line toward that
        // point, so a deeper target means it crosses into (and disappears
        // behind) the silhouette earlier in its final approach, instead of
        // hovering visibly just outside it until the convergence cuts off.
        // Checked against the real artwork: even this margin leaves ~4,900
        // candidate points, comfortably more than the star count needs, so
        // this rarely has to relax — the thinnest features (ear tips, the
        // tapered tail, the cap's own flatter plate) are what the fallbacks
        // are for.
        let candidates = silhouetteCandidates(data, cw, ch, 40, stride);
        if (candidates.length < count) candidates = silhouetteCandidates(data, cw, ch, 20, stride);
        if (candidates.length < count) candidates = silhouetteCandidates(data, cw, ch, 0, stride);
        const n = Math.min(count, candidates.length);
        for (let i = 0; i < n; i++) {
          const j = i + Math.floor(Math.random() * (candidates.length - i));
          [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
        }
        const picked = candidates.slice(0, count).map((p) => ({ nx: p.x / cw, ny: p.y / ch }));
        setPoints(picked);
      });
    };
    return () => {
      cancelled = true;
    };
  }, [count]);
  return points;
}

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

// Cached glow sprite for the main-thread fallback (the worker builds its own).
function makeGlowSprite(size, rgb) {
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  grad.addColorStop(0, `rgba(${rgb},0.95)`);
  grad.addColorStop(0.5, `rgba(${rgb},0.35)`);
  grad.addColorStop(1, `rgba(${rgb},0)`);
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  return c;
}

// Streak beat before the stars converge; the convergence clock only starts once
// the logo targets exist, so the field is always visible from the first frame.
const FORM_DELAY_MS = 1200;
const FORM_DURATION_MS = 4200;
// Logo fades in while the stars are on their last stretch so they slide in
// behind it; once converged they stay hidden there HOLD_MS, then clear.
// Keep both in sync with public/homeStarfield.worker.js.
const REVEAL_AT = 0.55;
const HOLD_MS = 1000;

// Below this width the logo is hidden (see the media query further down), so
// the WebGL scene only runs while the mark is on screen. Phones and tablets
// never mount this hero (Home.jsx sends them to HomeMobile).
const DESKTOP_QUERY = '(min-width: 1101px)';
const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function getUse3DLogo() {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DESKTOP_QUERY).matches && !window.matchMedia(REDUCE_MOTION_QUERY).matches;
}

function supportsOffscreen() {
  return (
    typeof window !== 'undefined' &&
    typeof window.Worker !== 'undefined' &&
    typeof window.OffscreenCanvas !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined' &&
    typeof HTMLCanvasElement.prototype.transferControlToOffscreen === 'function'
  );
}

// Warm-white star grains with a brass-gold glow, converging into the
// UniFreelancer mark. Runs in a Web Worker via OffscreenCanvas so page rendering
// can't jank it; falls back to the main thread where OffscreenCanvas is absent.
export default function HomeV1Hero({ children }) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const logoRef = useRef(null);
  const logoWrapRef = useRef(null);
  const tiltRafRef = useRef(null);
  const revealedRef = useRef(false);
  const points = useLogoPoints(STAR_COUNT);
  const brassLogo = useBrassLogo();
  const [use3DLogo, setUse3DLogo] = useState(getUse3DLogo);
  const logoSceneRef = useRef(null);

  const pointsRef = useRef(null);
  const workerRef = useRef(null);
  const teardownRef = useRef(null);
  const listenersRef = useRef(null);
  const applyPointsRef = useRef(null); // fallback: push targets in when ready
  const offscreenFailedRef = useRef(false);

  // Push logo points to whichever path is live, the moment they're sampled.
  // Only while the logo is on screen (above 1100px): without targets the stars
  // never converge and just keep streaming, which is what the half-screen
  // hero wants. Widening the window later hands them over then.
  useEffect(() => {
    pointsRef.current = points;
    if (!points) return undefined;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const send = () => {
      if (!mq.matches) return;
      workerRef.current?.postMessage({ type: 'points', points });
      applyPointsRef.current?.(points);
    };
    send();
    mq.addEventListener('change', send);
    return () => mq.removeEventListener('change', send);
  }, [points]);

  // ── Preferred path: animate on a worker thread, started on mount ──
  useEffect(() => {
    if (!supportsOffscreen()) return undefined;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;

    // A pending teardown means StrictMode is re-invoking after a fake cleanup —
    // cancel it and keep the already-running worker.
    if (teardownRef.current) {
      clearTimeout(teardownRef.current);
      teardownRef.current = null;
    }

    if (!workerRef.current) {
      let offscreen;
      try {
        offscreen = canvas.transferControlToOffscreen();
      } catch {
        offscreenFailedRef.current = true; // let the fallback effect take over
        return undefined;
      }

      const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
      const worker = new Worker(`${base}/homeStarfield.worker.js`);
      workerRef.current = worker;

      const readSize = () => ({
        width: Math.max(1, canvas.clientWidth || canvas.offsetWidth || wrap.clientWidth),
        height: Math.max(1, canvas.clientHeight || canvas.offsetHeight || wrap.clientHeight),
      });

      const { width, height } = readSize();
      // Start streaking now; include points only if they already happen to be ready.
      worker.postMessage(
        { type: 'init', canvas: offscreen, width, height, points: window.matchMedia(DESKTOP_QUERY).matches ? pointsRef.current : null, box: readLogoBox(wrap, logoWrapRef.current) },
        [offscreen]
      );

      worker.onmessage = (e) => {
        if (e.data?.type === 'reveal' && logoRef.current) {
          logoRef.current.style.opacity = '1';
          if (!revealedRef.current) {
            revealedRef.current = true;
            if (logoWrapRef.current) logoWrapRef.current.style.pointerEvents = 'auto';
          }
        }
      };

      let resizeRaf = null;
      const onResize = () => {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(() => {
          resizeRaf = null;
          worker.postMessage({ type: 'resize', ...readSize(), box: readLogoBox(wrap, logoWrapRef.current) });
        });
      };
      window.addEventListener('resize', onResize);

      const io = new IntersectionObserver(
        ([entry]) => worker.postMessage({ type: 'visible', visible: entry.isIntersecting }),
        { threshold: 0 }
      );
      io.observe(wrap);

      listenersRef.current = () => {
        window.removeEventListener('resize', onResize);
        if (resizeRaf) cancelAnimationFrame(resizeRaf);
        io.disconnect();
      };
    }

    return () => {
      // Defer so a synchronous StrictMode re-invoke can cancel it; a real
      // unmount lets it fire and terminate the worker.
      teardownRef.current = setTimeout(() => {
        teardownRef.current = null;
        listenersRef.current?.();
        listenersRef.current = null;
        workerRef.current?.terminate();
        workerRef.current = null;
      }, 0);
    };
  }, []);

  // ── Fallback path: no OffscreenCanvas — animate on the main thread ──
  useEffect(() => {
    if (supportsOffscreen() && !offscreenFailedRef.current) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');

    const glowSprite = makeGlowSprite(64, '216,183,107');
    const STAR_FILL = 'rgb(248,244,236)';

    let W = 1;
    let H = 1;
    let animId;
    let stars = [];
    let pts = window.matchMedia(DESKTOP_QUERY).matches ? pointsRef.current : null;
    let targetsReady = !!pts;
    let formStart = null;

    const MAX_Z = 900;
    const SPEED = 0.55;
    const FOCAL = 160;

    function respawn(s) {
      s.wx = (Math.random() - 0.5) * 2400;
      s.wy = (Math.random() - 0.5) * 2000;
      s.wz = Math.random() * MAX_Z;
    }

    function initStars() {
      stars = [];
      for (let i = 0; i < STAR_COUNT; i++) {
        const s = { r: Math.random() * 1.3 + 0.5, tx: 0, ty: 0 };
        respawn(s);
        s.wz = Math.random() * MAX_Z;
        stars.push(s);
      }
    }

    function layoutTargets() {
      if (!pts || !pts.length || !stars.length) return;
      const box = readLogoBox(wrapRef.current, logoWrapRef.current) || DEFAULT_LOGO_BOX;
      const boxX = box.nx * W;
      const boxY = box.ny * H;
      const boxW = box.nw * W;
      const boxH = box.nh * H;
      for (let i = 0; i < stars.length; i++) {
        const q = pts[i % pts.length];
        stars[i].tx = boxX + q.nx * boxW;
        stars[i].ty = boxY + q.ny * boxH;
      }
    }

    // Let the points effect hand targets in once the logo is sampled.
    applyPointsRef.current = (newPts) => {
      pts = newPts;
      layoutTargets();
      targetsReady = true;
    };

    let stopped = false;

    function resize() {
      W = canvas.width = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
      if (!stars.length) initStars();
      layoutTargets();
      if (stopped) {
        stopped = false;
        animId = requestAnimationFrame(draw);
      }
    }
    resize();
    window.addEventListener('resize', resize);

    let visible = true;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const wasVisible = visible;
        visible = entry.isIntersecting;
        if (visible && !wasVisible && !stopped) draw();
      },
      { threshold: 0 }
    );
    if (wrapRef.current) observer.observe(wrapRef.current);

    function draw(now) {
      if (!visible) return;
      const t = now ?? performance.now();
      if (targetsReady && formStart === null) formStart = t;
      const formRaw = targetsReady ? Math.min(1, Math.max(0, t - formStart - FORM_DELAY_MS) / FORM_DURATION_MS) : 0;

      if (formRaw >= 1) {
        // Fully converged and hidden behind the logo — hold the last frame
        // for HOLD_MS, then clear rather than leave the dots under the mark
        // forever (they show through the faint mobile watermark).
        setTimeout(() => ctx.clearRect(0, 0, W, H), HOLD_MS);
        if (logoRef.current) {
          logoRef.current.style.opacity = '1';
          if (!revealedRef.current) {
            revealedRef.current = true;
            if (logoWrapRef.current) logoWrapRef.current.style.pointerEvents = 'auto';
          }
        }
        stopped = true;
        return;
      }

      const p = easeOutCubic(formRaw);
      const cx = W / 2;
      const cy = H / 2;
      ctx.clearRect(0, 0, W, H);

      ctx.globalCompositeOperation = 'lighter';
      for (const s of stars) {
        s.wz -= SPEED;
        if (s.wz <= 1) respawn(s);
        const k = FOCAL / s.wz;
        const warpX = s.wx * k + cx;
        const warpY = s.wy * k + cy;
        const depth = 1 - s.wz / MAX_Z;
        const off = warpX < 0 || warpX > W || warpY < 0 || warpY > H;
        if (off && p < 0.02) {
          s.__off = true;
          continue;
        }
        const x = warpX + (s.tx - warpX) * p;
        const y = warpY + (s.ty - warpY) * p;
        s.__x = x;
        s.__y = y;
        s.__depth = depth;
        s.__off = false;
        if (p > 0.05) {
          const glow = 1.5 + p * 3;
          const d = glow * 2;
          ctx.globalAlpha = 0.9 * p;
          ctx.drawImage(glowSprite, x - glow, y - glow, d, d);
        }
      }

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = STAR_FILL;
      for (const s of stars) {
        if (s.__off) continue;
        const warpR = Math.max(0.25, s.__depth * 1.8);
        const warpAlpha = 0.12 + s.__depth * 0.78;
        const r = warpR + (s.r - warpR) * p;
        const alpha = warpAlpha + (1 - warpAlpha) * p;
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.arc(s.__x, s.__y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (logoRef.current) {
        // Fades via the element's own 0.4s opacity transition, same as the
        // worker path's 'reveal' message.
        const op = formRaw > REVEAL_AT ? '1' : '0';
        logoRef.current.style.opacity = op;
        if (op === '1' && !revealedRef.current) {
          revealedRef.current = true;
          if (logoWrapRef.current) logoWrapRef.current.style.pointerEvents = 'auto';
        }
      }

      animId = requestAnimationFrame(draw);
    }
    animId = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      observer.disconnect();
      applyPointsRef.current = null;
    };
  }, []);

  // Re-evaluate the 3D/flat split on viewport or reduced-motion changes —
  // e.g. rotating a tablet across the 1101px breakpoint, or toggling the OS
  // reduced-motion setting mid-session.
  useEffect(() => {
    const mqDesktop = window.matchMedia(DESKTOP_QUERY);
    const mqReduce = window.matchMedia(REDUCE_MOTION_QUERY);
    const update = () => setUse3DLogo(mqDesktop.matches && !mqReduce.matches);
    mqDesktop.addEventListener('change', update);
    mqReduce.addEventListener('change', update);
    return () => {
      mqDesktop.removeEventListener('change', update);
      mqReduce.removeEventListener('change', update);
    };
  }, []);

  // Mount/tear down the WebGL scene as the desktop/reduced-motion split
  // changes. `createHeroLogoScene` dynamic-imports three — `cancelled` covers
  // the case where use3DLogo flips back off (or the component unmounts)
  // before that import resolves. Also pauses the render loop (via
  // scene.setVisible) whenever the hero scrolls off screen or the tab is
  // backgrounded — the biggest single GPU cost here is rendering a frame
  // nobody can see.
  useEffect(() => {
    if (!use3DLogo) return undefined;
    const canvasEl = logoRef.current;
    const wrap = wrapRef.current;
    if (!canvasEl) return undefined;
    let cancelled = false;
    let scene = null;
    let io = null;

    function applyVisible() {
      if (scene) scene.setVisible(io ? io.isVisible : true, document.visibilityState !== 'hidden');
    }

    createHeroLogoScene({ canvas: canvasEl, svgUrl: unicornLogoSvg }).then((s) => {
      if (cancelled) {
        s.dispose();
        return;
      }
      scene = s;
      logoSceneRef.current = s;
      applyVisible();
    });

    if (wrap && typeof IntersectionObserver !== 'undefined') {
      io = { isVisible: true };
      const observer = new IntersectionObserver(([entry]) => {
        io.isVisible = entry.isIntersecting;
        applyVisible();
      }, { threshold: 0 });
      observer.observe(wrap);
      io.disconnect = () => observer.disconnect();
    }
    document.addEventListener('visibilitychange', applyVisible);

    return () => {
      cancelled = true;
      io?.disconnect();
      document.removeEventListener('visibilitychange', applyVisible);
      if (scene) scene.dispose();
      logoSceneRef.current = null;
    };
  }, [use3DLogo]);

  // Scroll-through-the-hero progress (0 at top, 1 once scrolled a full hero
  // height past it) drives the extra yaw/drift in heroLogoScene.js — the
  // video's "it moves as I go down the page" beat, on the object itself
  // instead of a CSS trick.
  useEffect(() => {
    if (!use3DLogo) return undefined;
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        const heroH = wrapRef.current?.offsetHeight || window.innerHeight;
        const t = Math.min(1, Math.max(0, window.scrollY / heroH));
        logoSceneRef.current?.setScroll(t);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [use3DLogo]);

  // Mouse-tracking tilt for the WebGL logo — feeds real 3D rotation in
  // heroLogoScene.js instead of the CSS `perspective()` trick below.
  const handle3DPointerMove = (e) => {
    const wrap = logoWrapRef.current;
    if (!wrap || !logoSceneRef.current) return;
    const rect = wrap.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    logoSceneRef.current.setPointer(nx, ny);
  };

  const handle3DPointerLeave = () => {
    logoSceneRef.current?.setPointer(0, 0);
  };

  // Mouse-tracking tilt: subtle perspective rotation on the logo itself,
  // following the cursor, springing back to flat on leave.
  const handleLogoMove = (e) => {
    const wrap = logoWrapRef.current;
    const img = logoRef.current;
    if (!wrap || !img) return;
    if (tiltRafRef.current) return;
    const { clientX, clientY } = e;
    tiltRafRef.current = requestAnimationFrame(() => {
      tiltRafRef.current = null;
      const rect = wrap.getBoundingClientRect();
      const px = (clientX - rect.left) / rect.width;
      const py = (clientY - rect.top) / rect.height;
      const rx = (0.5 - py) * 16;
      const ry = (px - 0.5) * 16;
      img.style.transition = 'transform 90ms linear';
      img.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) scale(1.035)`;
    });
  };

  const handleLogoLeave = () => {
    if (tiltRafRef.current) {
      cancelAnimationFrame(tiltRafRef.current);
      tiltRafRef.current = null;
    }
    const img = logoRef.current;
    if (img) {
      img.style.transition = 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)';
      img.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)';
    }
  };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'relative',
        height: '100dvh',
        overflow: 'hidden',
        background: '#12161C',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      <canvas
        ref={canvasRef}
        aria-hidden
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      />
      {/* Below ~1100px (a half-screen window) there's no room for the logo
          beside the copy, so it's dropped and the stars just keep streaming
          (2026-10-19; it used to recentre as a faint watermark behind the
          text). Home.jsx enlarges the headline at the same width. */}
      <style>{`
        @media (max-width: 1100px) {
          .uf-hero-logo { display: none; }
        }
      `}</style>
      {/* Bounds the logo's horizontal position to the same 1440px content
          column as the hero copy (max-w-page) instead of the full 100vw
          hero — on ultra-wide screens `right: 8%` of the viewport drifted
          the logo into the page's outer margin, well past the content
          column's own right edge. The hero copy occupies roughly the left
          third of this column, so centering the logo at the 2/3 mark puts
          it in the middle of the remaining space next to the copy. */}
      <div
        aria-hidden
        style={{ position: 'absolute', inset: 0, margin: '0 auto', width: '100%', maxWidth: '1440px', pointerEvents: 'none' }}
      >
        <div
          ref={logoWrapRef}
          className="uf-hero-logo"
          onMouseMove={use3DLogo ? handle3DPointerMove : handleLogoMove}
          onMouseLeave={use3DLogo ? handle3DPointerLeave : handleLogoLeave}
          style={{
            position: 'absolute',
            left: '66.667%',
            transform: 'translateX(calc(-50% + 5em))',
            top: '14%',
            height: '72%',
            aspectRatio: LOGO_ASPECT,
            // Above the hero content wrapper (which is full-width and z-10 for
            // its own stacking, even though its visible text/CTAs sit to the
            // left) so hover reaches the logo across its whole silhouette
            // instead of only the band above where that content starts.
            zIndex: 20,
            pointerEvents: 'none',
          }}
        >
          {/* Desktop/motion-ok: a real WebGL plane (heroLogoScene.js) so the
              tilt and scroll response are genuine 3D. Mobile and
              prefers-reduced-motion get the flat brass image below instead —
              same asset, same reveal fade, just no canvas or JS scene. */}
          {use3DLogo ? (
            <canvas
              ref={logoRef}
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                transition: 'opacity 0.4s ease-out',
                pointerEvents: 'none',
              }}
            />
          ) : (
          <img
            ref={logoRef}
            src={brassLogo || undefined}
            alt=""
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              transform: 'perspective(900px) rotateX(0deg) rotateY(0deg) scale(1)',
              transition: 'opacity 0.4s ease-out',
              pointerEvents: 'none',
            }}
          />
          )}
        </div>
      </div>
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 45%, rgba(18,22,28,0.72) 100%)',
          pointerEvents: 'none',
        }}
      />

      {children}

      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '180px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(18,22,28,0.85) 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
